import { useCallback } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { ButtonTheme, ButtonV2 } from 'builder/components/Button'
import Icon24, { Icon20 } from 'builder/components/Icon'
import { useI18n } from 'builder/hooks/useI18n'
import { AiResumeReviewResult as AiResumeReviewResultType } from 'builder/modules/resumeEditor'
import { TRANSLATION_KEY } from '../../constants'
import { getScoreMark } from '../../helpers'
import { ReviewSection } from '../../styles'
import {
  SectionTitle,
  SectionHeader,
  SectionContent,
  ScoreMarkHeadline,
  ScoreMarkDescription,
  Container,
  ReviewSectionScore,
} from './styles'

type Props = {
  data: AiResumeReviewResultType
  onRefresh?: () => void
}

export const AiResumeReviewResult = (props: Props) => {
  const {
    data: { scoreContent, scoreHeadline, topStrengths, priorityFixes, quickTips },
    onRefresh,
  } = props
  const { i18n } = useI18n()
  const scoreMark = getScoreMark(scoreHeadline)

  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh()
      trackInternalEvent('click_refresh', { label: 'ai_review' })
    }
  }, [onRefresh])

  return (
    <Container data-testid="ai-resume-review-result">
      <ReviewSectionScore>
        <SectionHeader>
          <SectionTitle>{i18n.t(`${TRANSLATION_KEY}.result.score`)}</SectionTitle>
          {!!onRefresh && (
            <ButtonV2
              size="small"
              theme={ButtonTheme.secondary}
              leadingIcon={<Icon24.TurnLeftRegular />}
              onClick={handleRefresh}
              data-testid="ai-resume-review-result-refresh-button"
            >
              {i18n.t(`${TRANSLATION_KEY}.result.refresh`)}
            </ButtonV2>
          )}
        </SectionHeader>
        <SectionContent>
          <ScoreMarkHeadline $mark={scoreMark}>
            <Icon20.Star />
            {i18n.t(`${TRANSLATION_KEY}.result.score_mark.${scoreMark}`)}
          </ScoreMarkHeadline>
          <ScoreMarkDescription dangerouslySetInnerHTML={{ __html: scoreContent }} />
        </SectionContent>
      </ReviewSectionScore>
      {topStrengths && (
        <ReviewSection>
          <SectionHeader>
            <SectionTitle>{i18n.t(`${TRANSLATION_KEY}.result.top_strengths`)}</SectionTitle>
          </SectionHeader>
          <SectionContent dangerouslySetInnerHTML={{ __html: topStrengths }} />
        </ReviewSection>
      )}
      {priorityFixes && (
        <ReviewSection>
          <SectionHeader>
            <SectionTitle>{i18n.t(`${TRANSLATION_KEY}.result.priority_fixes`)}</SectionTitle>
          </SectionHeader>
          <SectionContent dangerouslySetInnerHTML={{ __html: priorityFixes }} />
        </ReviewSection>
      )}
      {quickTips && (
        <ReviewSection>
          <SectionHeader>
            <SectionTitle>{i18n.t(`${TRANSLATION_KEY}.result.quick_tips`)}</SectionTitle>
          </SectionHeader>
          <SectionContent dangerouslySetInnerHTML={{ __html: quickTips }} />
        </ReviewSection>
      )}
    </Container>
  )
}
